import { PLACEHOLDER } from "@constants/globalStrings"

/**
 * Capitalizes the first letter of a string and makes the rest lowercase
 * @example
 * Input: "hELLo"
 * Output: "Hello"
 */
export function capitalizeFirstLetter(input: string): string {
  if ((input && input?.length === 0) || !input) {
    return input
  }

  return input.charAt(0).toUpperCase() + input.slice(1).toLowerCase()
}

/**
 * Converts a snake_case string to display format by replacing underscores with spaces and capitalizing first letter
 * @example
 * Input: "hello_world"
 * Output: "Hello world"
 */
export function snakeCaseToDisplayValue(input: string): string {
  if ((input && input?.length === 0) || !input) {
    return input
  }

  return capitalizeFirstLetter(input?.replace(/_/g, " "))
}

/**
 * Returns null if field matches placeholder, otherwise returns the field value
 * @example
 * Input: "PLACEHOLDER"
 * Output: null
 */
export function cleanField(field: string): string | null {
  return field === PLACEHOLDER ? null : field
}

/**
 * Formats a number to have 2 decimal places only if it has decimals
 * @example
 * Input: 123.456
 * Output: "123.46"
 * Input: 123
 * Output: "123"
 */
export function formatToTwoDecimals(value: number): string {
  return value % 1 === 0 ? `${value}` : `${value.toFixed(2)}`
}

/**
 * Converts PascalCase to Title Case by adding spaces before capital letters
 * @example
 * Input: "HelloWorld"
 * Output: "Hello World"
 */
export const pascalCaseToTitleCase = (str: string): string =>
  str.replace(/([A-Z][a-z])/g, " $1").trim()

/**
 * Converts camelCase to Sentence case by adding spaces and capitalizing first letter
 * @example
 * Input: "helloWorld"
 * Output: "Hello world"
 */
export const camelCaseToSentenceCase = (value: string): string => {
  // Add space before capital letters and convert to lowercase
  const withSpaces = value
    .replace(/([A-Z])/g, " $1")
    .toLowerCase()
    .trim()
  // Capitalize only the first letter of the entire string
  return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1)
}

/**
 * Converts any string format (snake_case, camelCase, PascalCase) to Title Case
 * @example
 * Input: "hello_world" or "helloWorld" or "HelloWorld"
 * Output: "Hello World"
 */
export const toTitleCase = (str: string): string => {
  // Replace underscores with spaces
  let modifiedStr = str?.replace(/_/g, " ")
  // Insert a space before any uppercase letter that follows a lowercase letter
  modifiedStr = modifiedStr?.replace(/([a-z])([A-Z])/g, "$1 $2")
  // Insert a space before any uppercase letter that follows another uppercase letter and is followed by a lowercase letter
  modifiedStr = modifiedStr?.replace(/([A-Z])([A-Z][a-z])/g, "$1 $2")
  // Capitalize the first letter of each word
  modifiedStr = modifiedStr?.replace(/\w\S*/g, (txt) => {
    return txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase()
  })
  return modifiedStr
}

/**
 * Converts a snake_case string to camelCase
 * @example
 * Input: "hello_world"
 * Output: "helloWorld"
 */
export const snakeCaseToCamelCase = (str: string): string =>
  str
    .replace(/^_+/, "") // Remove leading underscores
    .replace(/_+$/, "") // Remove trailing underscores
    .replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())

/**
 * Converts various string formats to sentence case while preserving abbreviations
 * Handles snake_case, camelCase, PascalCase, and preserves known abbreviations
 * @example
 * Input: "hello_world" → "Hello world"
 * Input: "helloWorld" → "Hello world"
 * Input: "Default BOM" → "Default BOM" (preserves abbreviation)
 * Input: "#PO331" → "#PO331" (preserves abbreviation with numbers)
 * Input: "MO207" → "MO207" (preserves abbreviation with numbers)
 */
export const toSentenceCase = (str: string): string => {
  if (!str) return str

  // Known abbreviations to preserve
  const abbreviations = ["MO", "PO", "SO", "WO", "BOM", "JSON", "API"]

  // First normalize the string by removing extra spaces but preserving single spaces
  let processedString = str.replace(/\s+/g, " ").trim()

  // Handle snake_case first: replace underscores with spaces
  processedString = processedString.replace(/_/g, " ")

  // Handle camelCase/PascalCase by adding spaces before uppercase letters
  processedString = processedString.replace(/([a-z\d])([A-Z])/g, "$1 $2")
  // Handle transitions from uppercase to uppercase+lowercase (like "XMLParser" → "XML Parser")
  processedString = processedString.replace(/([A-Z])([A-Z][a-z])/g, "$1 $2")

  // Now protect known abbreviations (they should be separated by spaces now)
  const abbreviationMap = new Map<string, string>()
  let placeholderIndex = 0

  // Replace each known abbreviation with a placeholder
  abbreviations.forEach((abbrev) => {
    // Create regex to match the abbreviation followed by optional numbers
    // Use word boundaries now that we've added spaces
    const regex = new RegExp(`\\b${abbrev}\\d*\\b`, "gi")
    processedString = processedString.replace(regex, (match) => {
      const placeholder = `zzabbrevzz${placeholderIndex}zzabbrevzz`
      // Preserve the original case and any numbers
      abbreviationMap.set(placeholder, match.toUpperCase())
      placeholderIndex += 1
      return placeholder
    })
  })

  // Handle transitions around placeholders
  processedString = processedString.replace(
    /([a-z\d])(zzabbrevzz\d+zzabbrevzz)/g,
    "$1 $2",
  )
  processedString = processedString.replace(
    /(zzabbrevzz\d+zzabbrevzz)([a-z])/g,
    "$1 $2",
  )

  // Apply sentence case: capitalize first letter, lowercase the rest
  // But don't modify placeholders
  const words = processedString.split(" ").filter((word) => word.length > 0)
  processedString = words
    .map((word, index) => {
      // Don't modify placeholder words
      if (word.includes("zzabbrevzz")) {
        return word
      }

      if (index === 0) {
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      }
      return word.toLowerCase()
    })
    .join(" ")

  // Restore all abbreviations
  abbreviationMap.forEach((abbrev, placeholder) => {
    processedString = processedString.replace(placeholder, abbrev)
  })

  return processedString
}
